import type { UsageEventRow } from "../api/getUsageEvents";

export interface UsageTotals {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  audioSeconds: number;
  costUsd: number;
  callCount: number;
}

export interface UsageGroup extends UsageTotals {
  key: string;
}

export interface DailyPoint extends UsageTotals {
  date: string;
}

export interface UsageSummary {
  totals: UsageTotals;
  byCapability: UsageGroup[];
  byModel: UsageGroup[];
  byProvider: UsageGroup[];
  byBilling: { platform: UsageTotals; user: UsageTotals };
  daily: DailyPoint[];
  rows: UsageEventRow[];
}

const CAPABILITY_LABELS: Record<string, string> = {
  roteiros: "Roteiros",
  consultas: "Consultas",
  criativos: "Criativos",
  simulador: "Simulador",
  decodificar: "Perfil do Cliente",
  pacientes: "Pacientes",
};

export function capabilityLabel(capability: string): string {
  return CAPABILITY_LABELS[capability] ?? capability;
}

function emptyTotals(): UsageTotals {
  return {
    inputTokens: 0,
    outputTokens: 0,
    totalTokens: 0,
    audioSeconds: 0,
    costUsd: 0,
    callCount: 0,
  };
}

function add(totals: UsageTotals, row: UsageEventRow): void {
  totals.inputTokens += row.input_tokens ?? 0;
  totals.outputTokens += row.output_tokens ?? 0;
  totals.totalTokens += (row.input_tokens ?? 0) + (row.output_tokens ?? 0);
  totals.audioSeconds += row.audio_seconds ?? 0;
  totals.costUsd += Number(row.cost_usd ?? 0);
  totals.callCount += 1;
}

function dayKey(iso: string): string {
  const d = new Date(iso);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function aggregateUsage(rows: UsageEventRow[]): UsageSummary {
  const totals = emptyTotals();
  const capability = new Map<string, UsageTotals>();
  const model = new Map<string, UsageTotals>();
  const provider = new Map<string, UsageTotals>();
  const platform = emptyTotals();
  const userBilling = emptyTotals();
  const daily = new Map<string, UsageTotals>();

  for (const row of rows) {
    add(totals, row);

    const cap = row.capability || "outros";
    if (!capability.has(cap)) capability.set(cap, emptyTotals());
    add(capability.get(cap)!, row);

    const mid = row.model_id || "—";
    if (!model.has(mid)) model.set(mid, emptyTotals());
    add(model.get(mid)!, row);

    const prov = row.provider || "—";
    if (!provider.has(prov)) provider.set(prov, emptyTotals());
    add(provider.get(prov)!, row);

    if (row.billing_mode === "user") add(userBilling, row);
    else add(platform, row);

    const dk = dayKey(row.created_at);
    if (!daily.has(dk)) daily.set(dk, emptyTotals());
    add(daily.get(dk)!, row);
  }

  const toGroups = (m: Map<string, UsageTotals>): UsageGroup[] =>
    [...m.entries()]
      .map(([key, t]) => ({ key, ...t }))
      .sort((a, b) => b.costUsd - a.costUsd || b.totalTokens - a.totalTokens);

  return {
    totals,
    byCapability: toGroups(capability),
    byModel: toGroups(model),
    byProvider: toGroups(provider),
    byBilling: { platform, user: userBilling },
    daily: [...daily.entries()]
      .map(([date, t]) => ({ date, ...t }))
      .sort((a, b) => a.date.localeCompare(b.date)),
    rows,
  };
}

export function dayKeyFromIso(iso: string): string {
  return dayKey(iso);
}
